/**
 * Pagina Gestione Fornitori Associazione
 * Permette agli admin di cercare, aggiungere e rimuovere seller
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  Trash2, 
  Building2, 
  Package,
  ShoppingCart,
  TrendingUp,
  Calendar,
  MoreVertical,
  LayoutGrid,
  TableIcon,
  Filter,
  ArrowUpDown,
  Star,
  StarOff,
  Mail,
  Phone,
  Eye,
  UserPlus,
  Loader2,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface SellerStats {
  total_orders: number;
  total_value: number;
}

interface Seller {
  id: string;
  company_id: string;
  association_id: string;
  seller_code?: string;
  categories: string[];
  is_preferred: boolean;
  is_active: boolean;
  notes?: string;
  created_at: string;
  company: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    logo_url?: string;
    address?: string;
  };
  stats?: SellerStats;
}

interface AvailableSeller {
  id: string;
  name: string;
  email: string;
  phone?: string;
  logo_url?: string;
  address?: string;
}

const AssociationSuppliersPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [availableSellers, setAvailableSellers] = useState<AvailableSeller[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  
  // Filtri e ordinamento
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    return (localStorage.getItem('suppliers_view_mode') as 'cards' | 'table') || 'cards';
  });
  
  // Modals
  const [addSellerOpen, setAddSellerOpen] = useState(false);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Seller | null>(null);
  
  const associationId = user?.association_id;
  
  // Tenant config per sapere se ordini sono abilitati
  const [ordersEnabled, setOrdersEnabled] = useState(true);
  
  useEffect(() => {
    const checkMenu = async () => {
      try {
        const res = await axios.get(`${API}/associations/my-association/menu`);
        if (res.data.is_association_user) {
          const menu = res.data.menu || {};
          setOrdersEnabled(menu.ordini?.visible !== false);
        }
      } catch { /* ignore */ }
    };
    checkMenu();
  }, []);

  // Salva preferenza view mode
  useEffect(() => {
    localStorage.setItem('suppliers_view_mode', viewMode);
  }, [viewMode]);

  // Fetch sellers
  const fetchSellers = useCallback(async () => {
    if (!associationId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      const res = await axios.get(`${API}/associations/${associationId}/sellers-with-stats?${params}`);
      setSellers(res.data.sellers || []);
      setTotal(res.data.total || 0);
      setCategories(res.data.available_categories || []);
    } catch (error) {
      console.error('Error fetching sellers:', error);
      toast.error('Errore nel caricamento dei fornitori');
    } finally {
      setLoading(false);
    }
  }, [associationId, search, selectedCategory, sortBy, sortOrder]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  // Fetch available sellers
  const fetchAvailableSellers = useCallback(async () => {
    if (!associationId) return;
    
    setLoadingAvailable(true);
    try {
      const params = new URLSearchParams();
      if (searchAvailable) params.append('search', searchAvailable);
      
      const res = await axios.get(`${API}/associations/${associationId}/available-sellers?${params}`);
      setAvailableSellers(res.data.sellers || []);
    } catch (error) {
      console.error('Error fetching available sellers:', error);
    } finally {
      setLoadingAvailable(false);
    }
  }, [associationId, searchAvailable]);

  useEffect(() => {
    if (addSellerOpen) {
      fetchAvailableSellers();
    }
  }, [addSellerOpen, fetchAvailableSellers]);

  // Aggiungi seller
  const handleAddSeller = async (companyId: string) => {
    if (!associationId) return;
    
    setAddingId(companyId);
    try {
      await axios.post(`${API}/associations/${associationId}/sellers/by-company`, {
        company_id: companyId
      });
      toast.success('Fornitore aggiunto con successo!');
      setAddSellerOpen(false);
      fetchSellers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta del fornitore');
    } finally {
      setAddingId(null);
    }
  };

  // Rimuovi seller
  const handleRemoveSeller = async () => {
    if (!associationId || !confirmRemove) return;
    
    setRemovingId(confirmRemove.id);
    try {
      await axios.delete(`${API}/associations/${associationId}/sellers/${confirmRemove.id}`);
      toast.success('Fornitore rimosso dall\'associazione');
      setConfirmRemove(null);
      fetchSellers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione');
    } finally {
      setRemovingId(null);
    }
  };

  // Toggle preferito
  const handleTogglePreferred = async (seller: Seller) => {
    if (!associationId) return;
    
    try {
      await axios.put(`${API}/associations/${associationId}/sellers/${seller.id}?is_preferred=${!seller.is_preferred}`);
      fetchSellers();
      toast.success(seller.is_preferred ? 'Rimosso dai preferiti' : 'Aggiunto ai preferiti');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Formatta valuta
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  // Formatta data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Card view component
  const SellerCard = ({ seller }: { seller: Seller }) => (
    <Card className="hover:shadow-md transition-shadow" data-testid={`seller-card-${seller.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-14 w-14 rounded-lg">
            <AvatarImage src={seller.company?.logo_url} />
            <AvatarFallback className="rounded-lg bg-primary/10 text-primary">
              {seller.company?.name?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold truncate">{seller.company?.name}</h3>
              {seller.is_preferred && (
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              )}
            </div>
            <p className="text-sm text-muted-foreground truncate">{seller.company?.email}</p>
            {seller.categories?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {seller.categories.slice(0, 3).map((cat, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {cat}
                  </Badge>
                ))}
                {seller.categories.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{seller.categories.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleTogglePreferred(seller)}>
                {seller.is_preferred ? (
                  <>
                    <StarOff className="h-4 w-4 mr-2" />
                    Rimuovi dai preferiti
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4 mr-2" />
                    Aggiungi ai preferiti
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-destructive"
                onClick={() => setConfirmRemove(seller)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Rimuovi fornitore
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              <strong>{seller.stats?.total_orders || 0}</strong> ordini
            </span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              <strong>{formatCurrency(seller.stats?.total_value || 0)}</strong>
            </span>
          </div>
        </div>
        
        {/* Data aggiunta */}
        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          Aggiunto il {formatDate(seller.created_at)}
        </div>
      </CardContent>
    </Card>
  );

  if (!associationId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Accesso non autorizzato</h2>
            <p className="text-muted-foreground">
              Questa pagina è riservata agli amministratori di associazione.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Fornitori Associazione
          </h1>
          <p className="text-muted-foreground">
            Gestisci i fornitori collegati alla tua associazione
          </p>
        </div>
        
        <Button onClick={() => setAddSellerOpen(true)} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi Fornitore
        </Button>
      </div>

      {/* Filters Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-suppliers-input"
              />
            </div>
            
            {/* Category Filter */}
            {categories.length > 0 && (
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Ordina" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Nome</SelectItem>
                <SelectItem value="orders">Ordini</SelectItem>
                <SelectItem value="value">Valore</SelectItem>
                <SelectItem value="date">Data aggiunta</SelectItem>
              </SelectContent>
            </Select>
            
            {/* View Toggle */}
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="rounded-r-none"
                data-testid="view-cards-btn"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="rounded-l-none"
                data-testid="view-table-btn"
              >
                <TableIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-sm text-muted-foreground">Fornitori totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {sellers.filter(s => s.is_preferred).length}
                </p>
                <p className="text-sm text-muted-foreground">Preferiti</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {ordersEnabled && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {sellers.reduce((acc, s) => acc + (s.stats?.total_orders || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Ordini totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
        
        {ordersEnabled && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(sellers.reduce((acc, s) => acc + (s.stats?.total_value || 0), 0))}
                </p>
                <p className="text-sm text-muted-foreground">Valore totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : sellers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun fornitore</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'Nessun fornitore corrisponde alla ricerca' : 'Aggiungi il primo fornitore all\'associazione'}
            </p>
            {!search && (
              <Button onClick={() => setAddSellerOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Fornitore
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sellers.map((seller) => (
            <SellerCard key={seller.id} seller={seller} />
          ))}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fornitore</TableHead>
                <TableHead>Categorie</TableHead>
                <TableHead className="text-right">Ordini</TableHead>
                <TableHead className="text-right">Valore</TableHead>
                <TableHead>Data Aggiunta</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sellers.map((seller) => (
                <TableRow key={seller.id} data-testid={`seller-row-${seller.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={seller.company?.logo_url} />
                        <AvatarFallback>
                          {seller.company?.name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{seller.company?.name}</span>
                          {seller.is_preferred && (
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">{seller.company?.email}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {seller.categories?.slice(0, 2).map((cat, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                      {seller.categories?.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{seller.categories.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {seller.stats?.total_orders || 0}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(seller.stats?.total_value || 0)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(seller.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleTogglePreferred(seller)}>
                          {seller.is_preferred ? (
                            <>
                              <StarOff className="h-4 w-4 mr-2" />
                              Rimuovi dai preferiti
                            </>
                          ) : (
                            <>
                              <Star className="h-4 w-4 mr-2" />
                              Aggiungi ai preferiti
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => setConfirmRemove(seller)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Rimuovi fornitore
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add Seller Dialog */}
      <Dialog open={addSellerOpen} onOpenChange={setAddSellerOpen}>
        <DialogContent className="max-w-lg" data-testid="add-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Aggiungi Fornitore
            </DialogTitle>
            <DialogDescription>
              Cerca e seleziona un fornitore da aggiungere all'associazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca fornitore per nome o email..."
                value={searchAvailable}
                onChange={(e) => setSearchAvailable(e.target.value)}
                className="pl-10"
                data-testid="search-available-input"
              />
            </div>
            
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {loadingAvailable ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : availableSellers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nessun fornitore disponibile</p>
                  <p className="text-sm">Tutti i fornitori sono già associati</p>
                </div>
              ) : (
                availableSellers.map((seller) => (
                  <div
                    key={seller.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={seller.logo_url} />
                        <AvatarFallback>
                          {seller.name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{seller.name}</p>
                        <p className="text-sm text-muted-foreground">{seller.email}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddSeller(seller.id)}
                      disabled={addingId === seller.id}
                      data-testid={`add-seller-${seller.id}`}
                    >
                      {addingId === seller.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Aggiungi
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Dialog */}
      <Dialog open={!!confirmRemove} onOpenChange={() => setConfirmRemove(null)}>
        <DialogContent data-testid="confirm-remove-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Conferma rimozione
            </DialogTitle>
            <DialogDescription>
              Sei sicuro di voler rimuovere <strong>{confirmRemove?.company?.name}</strong> dall'associazione?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRemoveSeller}
              disabled={removingId === confirmRemove?.id}
            >
              {removingId === confirmRemove?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Rimuovi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssociationSuppliersPage;
